import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AppLanguageService } from '@services/app-language.service';
import {
  AuthService,
  IEmailVerificationChallenge,
  ILoginPageModel,
  ISecondFactorChallenge,
} from '@services/auth.service';
import { map, switchMap, take } from 'rxjs';
import { AppSettings } from '../../shared/Constants/sharedConstants';
import {
  FORGOT_PASSWORD_PARAM,
  LOGIN_REASON_PARAM,
  LoginReason,
  RETURN_URL_PARAM,
  safeReturnUrl,
} from '../../utilities/return-url';
import {
  ForgotPasswordDialogComponent,
  ForgotPasswordDialogData,
} from './forgot-password-dialog/forgot-password-dialog.component';
import { VerificationEnd } from './email-verification-step/email-verification-step.component';
import { parseErrorBody, retryTime } from './login-responses';

/** The email-verification link carries both in the fragment, which never reaches a server (design 4.1). */
const LINK_CHALLENGE_PARAM = 'challenge';
const LINK_CODE_PARAM = 'code';
/** Present on a new-device email's link, for the step's wording. */
const LINK_KIND_PARAM = 'kind';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, AfterViewInit {
  readonly form = new FormGroup({
    userName: new FormControl('', { nonNullable: true }),
    password: new FormControl('', { nonNullable: true }),
  });

  readonly appStoreLink = AppSettings.UIAppConfig?.mobileAppLinks?.appleStore;
  readonly googlePlayLink = AppSettings.UIAppConfig?.mobileAppLinks?.googlePlay;

  showPassword = false;
  isLoggingIn = false;
  /**
   * A catalog key. The server refuses with one deliberately vague answer that never says which field
   * was wrong, so there is nothing to put under a single field.
   */
  loginError: 'Login.Rejected' | 'Login.Failed' | 'Login.CodeLimitEmails' | 'Login.CodeLimitWrongCodes' | null = null;
  /** When a code limit lifts, for the limit errors. */
  loginErrorParams: { time: string } | null = null;
  sessionExpired = false;
  /** Signed out in another tab or device, or by an administrator - see AuthService.expireSession. */
  signedOutElsewhere = false;
  /** The password was right and an emailed code must follow: the step replaces the form. */
  verification: IEmailVerificationChallenge | null = null;
  /** The password was right and a second factor must follow: the step replaces the form. */
  secondFactor: ISecondFactorChallenge | null = null;
  /** The code from the email link, filled in for the user to submit. */
  linkCode: string | null = null;
  codeExpired = false;
  tooManyCodes = false;
  finishedFromLink = false;

  constructor(
    private _as: AuthService,
    private _router: Router,
    private _cdr: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _ar: ActivatedRoute,
    private _language: AppLanguageService,
    private _location: Location,
    @Inject(LOCALE_ID) private _locale: string
  ) {}

  ngOnInit() {
    // AuthService.expireSession redirects here with a reason when a session ended under a tab that
    // did not choose to end it, so say why they are looking at a login form instead of their page.
    const reason = this._ar.snapshot.queryParamMap.get(LOGIN_REASON_PARAM) as LoginReason | null;
    this.sessionExpired = reason === 'expired';
    this.signedOutElsewhere = reason === 'signed-out-elsewhere';
    this.openFromEmailLink();

    // Signed out, so back to the signed-out language: this page is where a logout, an expired
    // session and a sign-out elsewhere all land, without a reload. A no-op straight after boot.
    this._language.use(null).pipe(take(1)).subscribe();
    // An already-authenticated visitor never gets here: AnonymousOnlyGuard redirects on the route.
  }

  /** "Request a new link" from a dead access link lands here with the dialog to open. */
  ngAfterViewInit() {
    if (this._ar.snapshot.queryParamMap.has(FORGOT_PASSWORD_PARAM)) {
      setTimeout(() => this.onForgotPasswordClick());
    }
  }

  onForgotPasswordClick(resetRequired = false) {
    this._dialog.open<ForgotPasswordDialogComponent, ForgotPasswordDialogData>(
      ForgotPasswordDialogComponent,
      {
        data: { resetRequired },
        width: '28rem',
        maxWidth: 'calc(100vw - 2rem)',
        autoFocus: 'first-tabbable',
      }
    );
  }

  submit() {
    if (this.isLoggingIn) {
      return;
    }
    const { userName, password } = this.form.getRawValue();
    // Once they submit, any login error replaces the notice rather than stacking under it.
    this.sessionExpired = false;
    this.signedOutElsewhere = false;
    this.codeExpired = false;
    this.tooManyCodes = false;
    this.finishedFromLink = false;
    this.loginError = null;
    this.isLoggingIn = true;
    this._cdr.markForCheck();

    this._as.login(userName, password).subscribe({
      next: response => {
        if (response?.emailVerification) {
          this.startVerification(response.emailVerification);
          return;
        }
        if (response?.secondFactor) {
          this.startSecondFactor(response.secondFactor);
          return;
        }
        if (response?.loginData?.error) {
          this.processLoginError(response);
          return;
        }
        this.processLoginSuccessResponse();
      },
      error: (error: HttpErrorResponse) => this.processLoginError(error),
    });
  }

  onVerified() {
    this.processLoginSuccessResponse();
  }

  /** The emailed check proved the address, and a second factor comes next (M18). */
  onSecondFactorNext(challenge: ISecondFactorChallenge) {
    this.verification = null;
    this.linkCode = null;
    this.startSecondFactor(challenge);
  }

  /** "Email me a code instead": the emailed-code step replaces this one (M17). */
  onEmailFallback(challenge: IEmailVerificationChallenge) {
    this.secondFactor = null;
    this.startVerification(challenge);
  }

  onVerificationEnded(end: VerificationEnd) {
    this.verification = null;
    this.secondFactor = null;
    this.linkCode = null;
    this.form.controls.password.reset();
    this._cdr.markForCheck();

    switch (end) {
      case 'expired':
        this.codeExpired = true;
        break;
      case 'too-many-codes':
        this.tooManyCodes = true;
        break;
      case 'rejected':
        this.loginError = 'Login.Rejected';
        break;
      case 'reset-required':
        this.onForgotPasswordClick(true);
        break;
      case 'completed':
        // Finished from the email link. Opened in another tab of this browser, the link left its
        // refresh cookie here too, so this tab can carry on; on another device it cannot.
        this._as.resumeFromRefreshCookie().subscribe(resumed => {
          if (resumed) {
            this.processLoginSuccessResponse();
            return;
          }
          this.finishedFromLink = true;
          this._cdr.markForCheck();
        });
        break;
    }
  }

  private startVerification(challenge: IEmailVerificationChallenge) {
    this.isLoggingIn = false;
    if (challenge.limitReached) {
      this.loginError = challenge.limit === 'wrong-codes' ? 'Login.CodeLimitWrongCodes' : 'Login.CodeLimitEmails';
      this.loginErrorParams = { time: retryTime(challenge.retryAfter, this._locale) };
    } else {
      this.verification = challenge;
    }
    this._cdr.markForCheck();
  }

  private startSecondFactor(challenge: ISecondFactorChallenge) {
    this.isLoggingIn = false;
    if (challenge.limitReached) {
      this.loginError = 'Login.CodeLimitWrongCodes';
      this.loginErrorParams = { time: retryTime(challenge.retryAfter, this._locale) };
    } else {
      this.secondFactor = challenge;
    }
    this._cdr.markForCheck();
  }

  /** Fills the code in from the email link, and takes the fragment out of the address bar and history. */
  private openFromEmailLink() {
    const fragment = new URLSearchParams(this._ar.snapshot.fragment ?? '');
    const challenge = fragment.get(LINK_CHALLENGE_PARAM);
    const code = fragment.get(LINK_CODE_PARAM);
    if (!challenge || !code) {
      return;
    }
    this._location.replaceState(this._location.path(false));
    this.verification = {
      challenge,
      addressHint: null,
      addressRequired: false,
      limitReached: false,
      // Wording only: the server knows the challenge's kind.
      kind: fragment.get(LINK_KIND_PARAM) === 'new-device' ? 'new-device' : 'email-verification',
    };
    this.linkCode = code;
  }

  private processLoginError(response: ILoginPageModel | HttpErrorResponse) {
    this.isLoggingIn = false;
    this._cdr.markForCheck();

    if (!(response instanceof HttpErrorResponse)) {
      this.loginError = 'Login.Rejected';
      return;
    }

    // A refusal is the token endpoint's LoginResult; anything else (a server fault, a proxy's error
    // page, the network) is a failure to ask, not an answer.
    const body = parseErrorBody(response.error);

    // The password was right but has expired, and the org wants it reset by email rather than
    // changed after signing in - so go straight to the form that does that.
    if (body?.passwordResetRequired) {
      this.onForgotPasswordClick(true);
      return;
    }

    this.loginError = body?.success === false ? 'Login.Rejected' : 'Login.Failed';
  }

  private processLoginSuccessResponse() {
    this._as.loggedIn$.next(true);
    // A page they were sent here from beats their homepage preference.
    const returnUrl = safeReturnUrl(this._ar.snapshot.queryParamMap.get(RETURN_URL_PARAM));
    this._as
      .getUserInformation(true)
      .pipe(
        take(1),
        // The strings loaded at boot were en-US: there was no token yet. Switch to the user's
        // language before navigating, so the first page's instant() calls already get it.
        switchMap(userInfo => this._language.use(userInfo?.cultureCode).pipe(map(() => userInfo))),
        switchMap(userInfo =>
          this._as.resumeExpiredSession(userInfo).pipe(map(canReturn => ({ userInfo, canReturn })))
        )
      )
      .subscribe({
        next: ({ userInfo, canReturn }) => {
          if (userInfo && userInfo.mustChangePassword) {
            return;
          }

          if (returnUrl && canReturn) {
            this._router.navigateByUrl(returnUrl);
            return;
          }

          const preferredHomepage = userInfo?.userPreferences?.homepage;
          const homepage =
            preferredHomepage === 'dashboard' || preferredHomepage === 'tank'
              ? preferredHomepage
              : 'dashboard';

          this._router.navigate([homepage]);
        },
        error: () => {
          // Without a profile the user cannot be matched to an expired session, so only an
          // ordinary deep link is still followed.
          this._as
            .resumeExpiredSession(null)
            .pipe(take(1))
            .subscribe(canReturn => {
              if (returnUrl && canReturn) {
                this._router.navigateByUrl(returnUrl);
              } else {
                this._router.navigate(['dashboard']);
              }
            });
        },
      });
  }
}
